import { useTranslation } from 'react-i18next'
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { AlertTriangle, Trash2 } from 'lucide-react'
import type { Layer } from '@/types/entities'

interface LayerDeleteDialogProps {
    open: boolean
    onOpenChange: (open: boolean) => void
    layer: Layer | null
    onConfirm: () => void
    isLoading?: boolean
}

export function LayerDeleteDialog({
    open,
    onOpenChange,
    layer,
    onConfirm,
    isLoading = false,
}: LayerDeleteDialogProps) {
    const { t } = useTranslation()
    if (!layer) return null

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="sm:max-w-[500px]">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <Trash2 className="h-5 w-5 text-destructive" />
                        {t('layers.deleteLayerTitle')}
                    </DialogTitle>
                    <DialogDescription>
                        {t('layers.confirmDelete')} {layer.layerID !== null ? `Layer ID ${layer.layerID}` : 'this layer'}?
                    </DialogDescription>
                </DialogHeader>

                <div className="space-y-4">
                    {/* Warning about layer IDs */}
                    <Alert>
                        <AlertTriangle className="h-4 w-4" />
                        <AlertDescription>
                            {t('layers.deleteWarning')}
                        </AlertDescription>
                    </Alert>

                    {/* Layer Details */}
                    <div className="bg-muted p-3 rounded-lg">
                        <div className="text-sm space-y-1">
                            <div><strong>{t('layers.layerID')}:</strong> {layer.layerID !== null ? layer.layerID : '-'}</div>
                            {layer.description && (
                                <div><strong>{t('layers.description')}:</strong> {layer.description}</div>
                            )}
                        </div>
                    </div>
                </div>

                <DialogFooter>
                    <Button
                        variant="outline"
                        onClick={() => onOpenChange(false)}
                        disabled={isLoading}
                    >
                        {t('common.cancel')}
                    </Button>
                    <Button
                        variant="destructive"
                        onClick={onConfirm}
                        disabled={isLoading}
                    >
                        {isLoading ? t('layers.deleting') : t('layers.deleteLayer')}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    )
}

